// src/pages/admin/Suppliers/Suppliers.jsx
import { useState, useEffect } from 'react'
import { Plus, Eye, Edit, Trash2, Search, Filter, X, RefreshCw, CheckCircle, XCircle } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import PageLoader from '../../../components/ui/PageLoader'
import SuppliersService from '../../../services/suppliersService'
import CategoryService from '../../../services/categoryService'
import CreateSupplierModal from '../../../components/modals/CreateSupplierModal'
import EditSupplierModal from '../../../components/modals/EditSupplierModal'
import ViewSupplierModal from '../../../components/modals/ViewSupplierModal'
import DeleteConfirmModal from '../../../components/modals/DeleteConfirmModal'
import { cn } from '../../../utils/cn'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-2 ${
    type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
    <span>{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2">
      <X className="h-4 w-4" />
    </button>
  </div>
)

const Suppliers = () => {
  // State management
  const [suppliers, setSuppliers] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [categoriesLoading, setCategoriesLoading] = useState(false)
  const [error, setError] = useState(null)
  const [toast, setToast] = useState(null)
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedSupplier, setSelectedSupplier] = useState(null)
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [filteredSuppliers, setFilteredSuppliers] = useState([])

  // Helper function to safely get nested values
  const safeGet = (obj, path, defaultValue = null) => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Helper function to validate supplier object
  const validateSupplier = (supplier) => {
    if (!supplier || typeof supplier !== 'object') return false
    return supplier._id && supplier.name && supplier.mobile
  }

  // Helper function to validate category object
  const validateCategory = (category) => {
    if (!category || typeof category !== 'object') return false
    return category._id && category.name
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // Fetch categories for filter dropdown
  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true)
      console.log('📦 Fetching categories for filter...')
      
      const response = await CategoryService.getAllCategories()
      
      if (response && response.success) {
        const categoriesData = safeGet(response, 'data.categories', [])
        const validCategories = Array.isArray(categoriesData) 
          ? categoriesData.filter(validateCategory)
          : []
        
        console.log('📦 Categories loaded for filter:', validCategories.length)
        setCategories(validCategories)
      } else {
        console.warn('⚠️ Failed to fetch categories:', response)
        setCategories([])
      }
    } catch (error) {
      console.error('❌ Error fetching categories:', error)
      setCategories([])
    } finally {
      setCategoriesLoading(false)
    }
  }

  // Fetch suppliers using service
  const fetchSuppliers = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await SuppliersService.getAllSuppliers({ page: 1, limit: 100 })
      
      if (response && response.success) {
        const suppliersData = safeGet(response, 'data.suppliers', []) || 
                            safeGet(response, 'data.docs', []) || 
                            safeGet(response, 'data', [])
        
        console.log('🏢 Suppliers response structure:', response.data)
        console.log('🏢 Extracted suppliers data:', suppliersData)
        
        // Log sample supplier structure
        if (suppliersData.length > 0) {
          console.log('🏢 Sample supplier structure:', suppliersData[0])
        }
        
        // Filter out invalid suppliers
        const validSuppliers = Array.isArray(suppliersData) 
          ? suppliersData.filter(validateSupplier)
          : []
        
        console.log('🏢 Valid suppliers loaded:', validSuppliers.length)
        setSuppliers(validSuppliers)
      } else {
        console.warn('⚠️ Unexpected response format:', response)
        setError('Failed to load suppliers')
        setSuppliers([])
      }
    } catch (error) {
      console.error('❌ Error fetching suppliers:', error)
      setError(error.message || 'Failed to load suppliers')
      setSuppliers([])
    } finally {
      setLoading(false)
    }
  }

  // Filter suppliers by category (if suppliers have category relationship)
  const filterSuppliersByCategory = (suppliers, categoryId) => {
    if (!categoryId) return suppliers
    
    return suppliers.filter(supplier => {
      // Assuming suppliers might have a category field or categories array
      // Adjust this logic based on your actual supplier-category relationship
      if (supplier.category) {
        return typeof supplier.category === 'object' 
          ? supplier.category._id === categoryId
          : supplier.category === categoryId
      }
      
      if (supplier.categories && Array.isArray(supplier.categories)) {
        return supplier.categories.some(cat => 
          typeof cat === 'object' ? cat._id === categoryId : cat === categoryId
        )
      }
      
      // If no category relationship exists, return false
      return false
    })
  }

  // Handle search and filter
  useEffect(() => {
    let filtered = suppliers

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = SuppliersService.searchSuppliersLocally(filtered, searchTerm)
    }

    // Apply category filter
    if (categoryFilter) {
      filtered = filterSuppliersByCategory(filtered, categoryFilter)
    }

    // Apply status filter
    if (statusFilter !== '') {
      const isActive = statusFilter === 'active'
      filtered = SuppliersService.filterSuppliersByStatus(filtered, isActive)
    }

    setFilteredSuppliers(filtered)
  }, [searchTerm, statusFilter, categoryFilter, suppliers])

  // Load data on component mount
  useEffect(() => {
    fetchSuppliers()
    fetchCategories()
  }, [])

  // Handle create supplier
  const handleCreateSupplier = async (supplierData) => {
    try {
      const response = await SuppliersService.createSupplier(supplierData)
      
      if (response && response.success) {
        showToast('Supplier created successfully', 'success')
        setShowCreateModal(false)
        fetchSuppliers() // Refresh the list
      } else {
        throw new Error(response?.message || 'Failed to create supplier')
      }
    } catch (error) {
      console.error('❌ Error creating supplier:', error)
      
      // Handle different types of errors
      let errorMessage = 'Failed to create supplier'
      
      if (error.response?.data?.message) {
        // Backend validation error
        errorMessage = error.response.data.message
      } else if (error.message) {
        // Network or other error
        errorMessage = error.message
      }
      
      // Show toast with backend error message
      showToast(errorMessage, 'error')
      
      // Re-throw the error so the modal can handle field-specific errors
      throw error
    }
  }

  // Handle edit supplier
  const handleEditSupplier = async (supplierData) => {
    try {
      if (!selectedSupplier?._id) {
        throw new Error('No supplier selected for editing')
      }
      
      const response = await SuppliersService.updateSupplier(selectedSupplier._id, supplierData)
      
      if (response && response.success) {
        showToast('Supplier updated successfully', 'success')
        setShowEditModal(false)
        setSelectedSupplier(null)
        fetchSuppliers() // Refresh the list
      } else {
        throw new Error(response?.message || 'Failed to update supplier')
      }
    } catch (error) {
      console.error('❌ Error updating supplier:', error)
      
      // Handle different types of errors
      let errorMessage = 'Failed to update supplier'
      
      if (error.response?.data?.message) {
        // Backend validation error
        errorMessage = error.response.data.message
      } else if (error.message) {
        // Network or other error
        errorMessage = error.message
      }
      
      // Show toast with backend error message
      showToast(errorMessage, 'error')
      
      // Re-throw the error so the modal can handle field-specific errors
      throw error
    }
  }

  // Handle delete supplier
  const handleDeleteSupplier = async () => {
    try {
      if (!selectedSupplier?._id) {
        throw new Error('No supplier selected for deletion')
      }
      
      const response = await SuppliersService.deleteSupplier(selectedSupplier._id)
      
      if (response && response.success) {
        showToast('Supplier deleted successfully', 'success')
        setShowDeleteModal(false)
        setSelectedSupplier(null)
        fetchSuppliers() // Refresh the list
      } else {
        throw new Error(response?.message || 'Failed to delete supplier')
      }
    } catch (error) {
      console.error('❌ Error deleting supplier:', error)
      
      // Handle different types of errors
      let errorMessage = 'Failed to delete supplier'
      
      if (error.response?.data?.message) {
        // Backend validation error
        errorMessage = error.response.data.message
      } else if (error.message) {
        // Network or other error
        errorMessage = error.message
      }
      
      // Show toast with backend error message
      showToast(errorMessage, 'error')
    }
  }

  // Handle view supplier
  const handleViewSupplier = (supplier) => {
    setSelectedSupplier(supplier)
    setShowViewModal(true)
  }

  // Handle edit supplier click
  const handleEditSupplierClick = (supplier) => {
    setSelectedSupplier(supplier)
    setShowEditModal(true)
  }

  // Handle delete supplier click
  const handleDeleteSupplierClick = (supplier) => {
    setSelectedSupplier(supplier)
    setShowDeleteModal(true)
  }

  // Handle filter changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
  }

  const handleCategoryFilterChange = (e) => {
    setCategoryFilter(e.target.value)
  }

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value)
  }

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('')
    setStatusFilter('')
    setCategoryFilter('')
  }

  // Simple refresh function
  const handleRefresh = async () => {
    console.log('🔄 Manual refresh triggered')
    setLoading(true)
    
    try {
      // Fetch fresh data
      const [suppliersResponse, categoriesResponse] = await Promise.all([
        SuppliersService.getAllSuppliers({ page: 1, limit: 100 }),
        CategoryService.getAllCategories()
      ])
      
      // Process suppliers
      if (suppliersResponse && suppliersResponse.success) {
        const suppliersData = safeGet(suppliersResponse, 'data.suppliers', []) || 
                            safeGet(suppliersResponse, 'data.docs', []) || 
                            safeGet(suppliersResponse, 'data', [])
        const validSuppliers = Array.isArray(suppliersData) 
          ? suppliersData.filter(validateSupplier)
          : []
        setSuppliers(validSuppliers)
      }

      // Process categories
      if (categoriesResponse && categoriesResponse.success) {
        const categoriesData = safeGet(categoriesResponse, 'data.categories', [])
        const validCategories = Array.isArray(categoriesData) 
          ? categoriesData.filter(validateCategory)
          : []
        setCategories(validCategories)
      }
      
    } catch (error) {
      console.error('❌ Error during refresh:', error)
      showToast('Error refreshing data', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString()
    } catch {
      return 'N/A'
    }
  }

  // Loading state
  if (loading && suppliers.length === 0) {
    return (
      <PageLoader 
        headerTitle="Suppliers"
        title="Loading suppliers..."
      />
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Toast Notification */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Suppliers</h1>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={handleRefresh}
              disabled={loading}
              leftIcon={RefreshCw}
              variant="outline"
              className="bg-white border-gray-300"
            >
              Refresh
            </Button>
            <Button
              onClick={() => setShowCreateModal(true)}
              leftIcon={Plus}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Add Supplier
            </Button>
          </div>
        </div>

        {/* Filters and Search */}
        <Card className="mb-6">
          <CardContent className="p-6">
            {/* Filter Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Filter Suppliers</h2>
              </div>
              {(searchTerm || categoryFilter || statusFilter) && (
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="text-orange-500 border-orange-500 hover:bg-orange-50"
                >
                  Clear Filters
                </Button>
              )}
            </div>

            {/* Filter Controls */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Search */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <Input
                  type="text"
                  placeholder="Search by name, mobile, email..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="pl-10 h-11 border-gray-300 w-full"
                />
              </div>

              {/* Category Filter */}
              {/* <div>
                <select
                  value={categoryFilter}
                  onChange={handleCategoryFilterChange}
                  disabled={categoriesLoading}
                  className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  <option value="">
                    {categoriesLoading ? 'Loading categories...' : 'All Categories'}
                  </option>
                  {categories.filter(cat => cat.isActive).map(category => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div> */}

              {/* Status Filter */}
              <div>
                <select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* Results Count */}
            <div className="text-sm text-gray-600">
              Showing {filteredSuppliers.length} of {suppliers.length} suppliers
            </div>
          </CardContent>
        </Card>

        {/* Error State */}
        {error && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="text-red-800">
                <strong>Error:</strong> {error}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Suppliers Table */}
        <Card className="bg-white">
          <CardContent className="p-0">
            {/* Empty State */}
            {!loading && filteredSuppliers.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-500 text-lg mb-2">
                  {suppliers.length === 0 ? 'No suppliers found' : 'No suppliers match your filters'}
                </div>
                <p className="text-gray-400 mb-4">
                  {suppliers.length === 0 
                    ? 'Get started by creating your first supplier.' 
                    : 'Try adjusting your search criteria.'
                  }
                </p>
                {suppliers.length === 0 && (
                  <Button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Supplier
                  </Button>
                )}
              </div>
            )}

            {/* Suppliers Table */}
            {!loading && filteredSuppliers.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mobile number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Purchases (Rs.)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredSuppliers.map((supplier, index) => (
                      <tr key={supplier._id || index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {supplier.name || 'Unknown Supplier'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {supplier.contactPerson || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {supplier.mobile || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {supplier.email || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {SuppliersService.formatCurrency(supplier.totalPurchases || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={cn(
                            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                            supplier.isActive !== false 
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          )}>
                            {supplier.isActive !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewSupplier(supplier)}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded"
                              title="View Supplier"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => handleEditSupplierClick(supplier)}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded"
                              title="Edit Supplier"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteSupplierClick(supplier)}
                              className="text-gray-400 hover:text-red-600 p-1 rounded"
                              title="Delete Supplier"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modals */}
        <CreateSupplierModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateSupplier}
        />

        {showEditModal && selectedSupplier && (
          <EditSupplierModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false)
              setSelectedSupplier(null)
            }}
            onSubmit={handleEditSupplier}
            supplier={selectedSupplier}
          />
        )}

        {showViewModal && selectedSupplier && (
          <ViewSupplierModal
            isOpen={showViewModal}
            onClose={() => {
              setShowViewModal(false)
              setSelectedSupplier(null)
            }}
            supplier={selectedSupplier}
          />
        )}

        {showDeleteModal && selectedSupplier && (
          <DeleteConfirmModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false)
              setSelectedSupplier(null)
            }}
            onConfirm={handleDeleteSupplier}
            itemName={selectedSupplier.name}
            itemType="Supplier"
          />
        )}
      </div>
    </div>
  )
}

export default Suppliers